<script lang="ts" context="module">
  // keep track of the sort direction, only one runner can be sorted at a time
  export const sortedRunner = writable<SortDirection | undefined>(undefined);
</script>

<script lang="ts">
  import { page } from '$app/stores';
  import type { Runner } from '$types/races';
  import { isSubscribed } from '$utils/auth';
  import { isVacantBox } from '$utils/race';
  import toast from 'svelte-french-toast';
  import { writable } from 'svelte/store';
  import ResultsBadge from './ResultsBadge.svelte';
  import RunnerSortBadge, {
    type SortDirection
  } from './RunnerSortBadge.svelte';

  import HasAlertIcon from '$components/HasAlertIcon.svelte';
  import SelectionSummaryDialog from '$lib/components/selection-summary-dialog/SelectionSummaryDialog.svelte';

  $: ({ race, priceAlerts } = $page.data);

  export let runner: Runner;

  let hovered = false;

  function handleClick() {
    // don't filter out vacant boxes or scratched runners
    if (isVacantBox(runner) || runner.scratchedTime) {
      return;
    }

    // dont let unsubscribed users sort
    if (!$isSubscribed) {
      toast('You must be subscribed to sort prices!', {
        icon: '🔒',
        duration: 2000,
        position: 'bottom-center'
      });
      return;
    }

    // check if the sorted runner is the same as the current runner
    if ($sortedRunner?.runnerId === runner.id) {
      // if it is, toggle the sort direction
      if ($sortedRunner.direction === 'desc') {
        sortedRunner.set({ runnerId: runner.id, direction: 'asc' });
      } else {
        sortedRunner.set(undefined);
      }
    } else {
      // if it's not, set the sort direction to descending
      sortedRunner.set({ runnerId: runner.id, direction: 'desc' });
    }
  }

  $: hasAlert = $priceAlerts?.data?.priceAlerts.some(
    (r) => r.runnerId === runner.id
  );
</script>

{#if $race}
  <SelectionSummaryDialog
    raceId={$race.id}
    dateTo={$race.meeting.date}
    code={$race.meeting.type}
    selectionName={runner.name}
    trainerName={runner.trainerName}
    class="flex"
  >
    <th
      on:click={handleClick}
      on:mouseenter={() => {
        if (runner.scratchedTime || isVacantBox(runner)) return;
        hovered = true;
      }}
      on:mouseleave={() => {
        hovered = false;
      }}
      class="z-10 w-full cursor-pointer select-none bg-gray-50 pr-4 text-left text-xs font-medium text-gray-900 hover:bg-gray-100 hover:text-gray-800 md:sticky md:left-0"
    >
      {#if runner}
        <div
          class="ml-2 flex items-center gap-x-2 {isVacantBox(runner)
            ? 'my-1'
            : ''}"
        >
          <div
            class="flex flex-row text-center {runner.scratchedTime ||
            isVacantBox(runner)
              ? 'text-gray-600'
              : 'text-black'}"
          >
            {#if $race.meeting.type === 'Greyhound'}
              {@const regionModifier = ['GBR', 'IRL'].includes(
                $race.meeting.location
              )
                ? '-eu-'
                : '-'}
              <div
                class="mr-2 h-5 w-5 rounded border border-slate-900/40 selection-g{regionModifier}{runner.number}
                {runner.scratchedTime || isVacantBox(runner)
                  ? 'opacity-30'
                  : ''}"
                aria-hidden="true"
              />
            {/if}
            <div class="w-5 self-center font-black">
              {runner.number}
            </div>
          </div>
          <div class="flex grow flex-col truncate">
            <div class="flex flex-row">
              <span
                class={runner.scratchedTime || isVacantBox(runner)
                  ? 'italic text-gray-500 line-through'
                  : ''}>{runner.name}</span
              >
              {#if runner.barrier && $race.meeting.type === 'Thoroughbred'}
                <span
                  class={runner.scratchedTime || isVacantBox(runner)
                    ? 'px-1 italic text-gray-500 line-through'
                    : 'px-1 italic text-gray-700'}>({runner.barrier})</span
                >
              {/if}
            </div>
            {#if $race.meeting.type === 'Greyhound'}
              <!-- this will be only a trainer -->
              <span class="text-[10px] italic text-gray-500"
                >{runner?.riderName
                  ? runner.riderName
                  : runner?.trainerName ?? ''}</span
              >
            {:else}
              <!-- this will be a driver and trainer -->
              {@const abbreviation =
                $race.meeting.type === 'Harness' ? 'D' : 'J'}
              <div class="flex gap-x-1">
                {#if runner?.riderName}
                  <span class="text-[10px] italic text-gray-500"
                    ><span class="mr-1 font-semibold">{abbreviation}</span
                    >{runner?.riderName ?? ''}</span
                  >
                {/if}
                {#if runner?.trainerName}
                  <span class="text-[10px] italic text-gray-500"
                    ><span class="mr-1 font-semibold">T</span
                    >{runner?.trainerName ?? ''}</span
                  >
                {/if}
              </div>
            {/if}
          </div>
          {#if hasAlert}
            <HasAlertIcon />
          {/if}
          <ResultsBadge runnerNumber={runner.number} race={$race} />
          <RunnerSortBadge
            {hovered}
            sortDirection={$sortedRunner?.runnerId === runner.id
              ? $sortedRunner
              : undefined}
          />
        </div>
      {/if}
    </th>
  </SelectionSummaryDialog>
{/if}
